import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  BookOpen, 
  TrendingUp, 
  Award, 
  Clock,
  ArrowRight,
  Menu,
  X,
  CheckCircle,
  Star,
  Download,
  Shield,
  Headphones,
  BadgeCheck,
  CreditCard,
  Play,
  FileText,
  Users
} from 'lucide-react';

const LandingPageDesign2 = ({ onNavigateToLogin }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [showDemoTest, setShowDemoTest] = useState(false);
  const [demoQuestions, setDemoQuestions] = useState([]);
  const [demoCurrentQ, setDemoCurrentQ] = useState(0);
  const [demoAnswers, setDemoAnswers] = useState({});
  const [demoSubmitted, setDemoSubmitted] = useState(false);
  const [demoScore, setDemoScore] = useState(0);
  const [showInstallModal, setShowInstallModal] = useState(false);
  
  // Counter animation states
  const [mockTests, setMockTests] = useState(0);
  const [students, setStudents] = useState(0);
  const [successRate, setSuccessRate] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [totalCourses, setTotalCourses] = useState(0);
  
  // Real stats from API
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [realStats, setRealStats] = useState({
    mock_tests: 100,
    questions: 500,
    students: 50,
    success_rate: 95,
    courses: 5
  });
  
  // Fetch real stats from API
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get('/public/stats');
        setRealStats(response.data);
        setStatsLoaded(true);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
        setStatsLoaded(true); // Use defaults
      }
    };
    fetchStats();
  }, []);
  
  // Animate counters when stats are loaded
  useEffect(() => {
    if (!statsLoaded) return;
    
    const duration = 2000; // 2 seconds
    const steps = 60;
    const mockTestTarget = realStats.mock_tests || 100;
    const studentsTarget = realStats.students || 50;
    const successRateTarget = realStats.success_rate || 95;
    const questionsTarget = realStats.questions || 500;
    const coursesTarget = realStats.courses || 5;
    
    const mockTestIncrement = mockTestTarget / steps;
    const studentsIncrement = studentsTarget / steps;
    const successRateIncrement = successRateTarget / steps;
    const questionsIncrement = questionsTarget / steps;
    const coursesIncrement = coursesTarget / steps;
    
    let currentStep = 0;
    
    const timer = setInterval(() => {
      currentStep++;
      
      if (currentStep <= steps) {
        setMockTests(Math.floor(mockTestIncrement * currentStep));
        setStudents(Math.floor(studentsIncrement * currentStep));
        setSuccessRate(Math.floor(successRateIncrement * currentStep));
        setTotalQuestions(Math.floor(questionsIncrement * currentStep));
        setTotalCourses(Math.floor(coursesIncrement * currentStep));
      } else {
        setMockTests(mockTestTarget);
        setStudents(studentsTarget);
        setSuccessRate(successRateTarget);
        setTotalQuestions(questionsTarget);
        setTotalCourses(coursesTarget);
        clearInterval(timer);
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, [statsLoaded, realStats]);

  useEffect(() => {
    fetchTestimonials();
    fetchFeaturedCourses();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get('/testimonials');
      setTestimonials(response.data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
      // Fallback to default testimonial if fetch fails
      setTestimonials([{
        id: '1',
        name: 'Manas Ranjan Kaunri',
        designation: 'CGDA Auditor',
        photo_url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Student',
        message: 'The mock tests were incredibly helpful! Questions cover each and every chapter.',
        rating: 5
      }]);
    }
  };

  const fetchFeaturedCourses = async () => {
    try {
      const response = await axios.get('/public/featured-courses');
      setFeaturedCourses(response.data);
    } catch (error) {
      console.error('Failed to fetch featured courses:', error);
    }
  };

  const startDemoTest = async () => {
    try {
      const response = await axios.get('/public/demo-questions');
      if (response.data.questions && response.data.questions.length > 0) {
        setDemoQuestions(response.data.questions);
        setDemoCurrentQ(0);
        setDemoAnswers({});
        setDemoSubmitted(false);
        setDemoScore(0);
        setShowDemoTest(true);
      }
    } catch (error) {
      console.error('Failed to fetch demo questions:', error);
    }
  };

  const handleDemoAnswer = (questionId, answer) => {
    setDemoAnswers({ ...demoAnswers, [questionId]: answer });
  };

  const submitDemoTest = () => {
    let correct = 0;
    demoQuestions.forEach((q) => {
      // correct_answer is 'a', 'b', 'c', 'd'
      if (demoAnswers[q.id] === q.correct_answer) {
        correct++;
      }
    });
    setDemoScore(correct);
    setDemoSubmitted(true);
  };

  // Helper to get options from question object
  const getQuestionOptions = (q) => {
    if (!q) return [];
    return [
      { key: 'a', value: q.option_a },
      { key: 'b', value: q.option_b },
      { key: 'c', value: q.option_c },
      { key: 'd', value: q.option_d }
    ].filter(opt => opt.value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_cgda-exam-prep/artifacts/bek6ttpm_logo.png" 
                alt="Departmental Exam"
                className="h-12 sm:h-14 md:h-16 object-contain"
              />
            </div>
            
            <div className="hidden md:flex items-center gap-3">
              <Button variant="ghost" onClick={() => setShowInstallModal(true)}>
                <Download className="w-4 h-4 mr-2" />
                Install App
              </Button>
              <Button variant="ghost" onClick={() => window.location.href = '/login'}>Login</Button>
              <Button className="bg-gradient-to-r from-blue-600 to-blue-700" onClick={() => window.location.href = '/register'}>Sign Up</Button>
            </div>

            <button 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-white">
            <div className="px-4 py-4 space-y-2">
              <Button variant="ghost" className="w-full" onClick={() => { setShowInstallModal(true); setMobileMenuOpen(false); }}>
                <Download className="w-4 h-4 mr-2" />
                Install App
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => window.location.href = '/login'}>Login</Button>
              <Button className="w-full bg-gradient-to-r from-blue-600 to-blue-700" onClick={() => window.location.href = '/register'}>Sign Up</Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Professional & Visual */}
      <section className="relative bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-700 py-6 sm:py-12 lg:py-24 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 4px 4px, white 10px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* Trust Badge - Always visible with blinking beacon */}
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/30 rounded-full px-3 py-1.5 sm:px-4 sm:py-2 mb-4">
                <span className="relative flex h-2.5 w-2.5 sm:h-3 sm:w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-90"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 sm:h-3 sm:w-3 bg-green-500"></span>
                </span>
                <span className="text-white text-xs sm:text-sm font-medium">India's Only Mock Test Platform for Govt Employees</span>
              </div>

              {/* Main Headline */}
              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                Ace Your <span className="text-yellow-300">Promotion Exams</span>
              </h1>

              {/* Subheadline */}
              <p className="text-base sm:text-lg text-blue-100 mb-6 max-w-xl mx-auto lg:mx-0">
                Smart mock tests, instant results & detailed analytics for government employees.
              </p>

              {/* CTAs - Compact on mobile */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-6">
                <Button 
                  size="sm" 
                  onClick={onNavigateToLogin} 
                  className="bg-white text-blue-600 hover:bg-gray-100 px-6 py-3 h-auto font-semibold shadow-xl"
                >
                  Start Learning <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={startDemoTest} 
                  className="border-2 border-white text-white hover:bg-white/10 px-6 py-3 h-auto font-semibold"
                >
                  Try Free Demo
                </Button>
              </div>

              {/* Social Proof Stats - Compact grid on mobile */}
              <div className="grid grid-cols-4 gap-2 sm:flex sm:flex-wrap sm:gap-6 justify-center lg:justify-start">
                <div className="text-center lg:text-left">
                  <div className="text-xl sm:text-2xl font-bold text-white">{mockTests}+</div>
                  <div className="text-xs text-blue-200">Tests</div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="text-xl sm:text-2xl font-bold text-white">{totalQuestions >= 1000 ? `${(totalQuestions/1000).toFixed(1)}K` : totalQuestions}+</div>
                  <div className="text-xs text-blue-200">MCQs</div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="text-xl sm:text-2xl font-bold text-white">{students >= 1000 ? `${(students/1000).toFixed(0)}K` : students}+</div>
                  <div className="text-xs text-blue-200">Students</div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="text-xl sm:text-2xl font-bold text-white">{successRate}%</div>
                  <div className="text-xs text-blue-200">Success</div>
                </div>
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative hidden lg:block">
              {/* Floating Card 1 - Score Card */}
              <div className="absolute top-0 right-0 bg-white rounded-2xl shadow-2xl p-6 w-64 z-20" style={{animation: 'float 6s ease-in-out infinite'}}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Test Score</p>
                    <p className="text-2xl font-bold text-gray-900">95/100</p>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{width: '95%'}}></div>
                </div>
                <p className="text-xs text-gray-500 mt-2">🎉 Excellent Performance!</p>
              </div>

              {/* Floating Card 2 - Progress Chart */}
              <div className="absolute bottom-12 left-0 bg-white rounded-2xl shadow-2xl p-6 w-72 z-20" style={{animation: 'float 6s ease-in-out infinite 1s'}}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Study Progress</p>
                    <p className="text-xs text-gray-500">Last 7 days</p>
                  </div>
                </div>
                <div className="flex items-end justify-between gap-1 h-20">
                  {[40, 60, 45, 80, 65, 95, 75].map((height, i) => (
                    <div key={i} className="flex-1 bg-gradient-to-t from-blue-500 to-blue-400 rounded-t" style={{height: `${height}%`}}></div>
                  ))}
                </div>
              </div>

              {/* Main Hero Image */}
              {/*<div className="relative z-10">
                <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20">
                  <img 
                    src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=500&fit=crop"
                    alt="Students preparing for exams"
                    className="rounded-2xl w-full h-96 object-cover shadow-2xl"
                  />
                </div>
              </div>*/}

              {/* Floating Icon 1 */}
              <div className="absolute top-1/4 -left-4 w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg" style={{animation: 'bounce-slow 4s ease-in-out infinite'}}>
                <Award className="w-8 h-8 text-white" />
              </div>

              {/* Floating Icon 2 */}
              <div className="absolute bottom-1/3 -right-4 w-20 h-20 bg-green-400 rounded-full flex items-center justify-center shadow-lg" style={{animation: 'bounce-slow 4s ease-in-out infinite 1s'}}>
                <BookOpen className="w-10 h-10 text-white" />
              </div>
            </div>

            {/* Mobile Hero Image */}
           {/*} <div className="lg:hidden">
              <img 
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop"
                alt="Students preparing for exams"
                className="rounded-2xl w-full h-64 object-cover shadow-2xl"
              />
            </div>*/}
          </div>
        </div>

        {/* Wave Separator */}
        <div className="absolute bottom-0 left-0 w-full">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Add custom animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
      `}</style>

      {/* Features Cards - Compact on mobile */}
      <section className="py-8 sm:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-6 sm:mb-10">
            <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2">
              Everything You Need to Succeed
            </h2>
            <p className="text-sm sm:text-base text-gray-600">
              Powerful features designed for your success
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-6">
            {[
              { icon: Clock, title: 'Real-Time Tests', desc: 'Timed tests like real exams', bgColor: 'bg-blue-50', borderColor: 'border-blue-200', iconBg: 'bg-gradient-to-br from-blue-500 to-blue-700' },
              { icon: TrendingUp, title: 'Analytics', desc: 'Track your progress', bgColor: 'bg-green-50', borderColor: 'border-green-200', iconBg: 'bg-gradient-to-br from-green-500 to-green-700' },
              { icon: Award, title: 'Instant Results', desc: 'Feedback with explanations', bgColor: 'bg-purple-50', borderColor: 'border-purple-200', iconBg: 'bg-gradient-to-br from-purple-500 to-purple-700' },
              { icon: CheckCircle, title: 'Smart Learning', desc: 'Questions for your level', bgColor: 'bg-orange-50', borderColor: 'border-orange-200', iconBg: 'bg-gradient-to-br from-orange-500 to-orange-700' }
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className={`p-3 sm:p-6 ${feature.bgColor} border ${feature.borderColor}`}>
                  <div className={`w-10 h-10 sm:w-14 sm:h-14 ${feature.iconBg} rounded-xl sm:rounded-2xl flex items-center justify-center mb-2 sm:mb-4 shadow-lg`}>
                    <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <h3 className="text-sm sm:text-lg font-bold text-gray-900 mb-1">{feature.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">{feature.desc}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust Badges Section - Compact */}
      <section className="py-4 sm:py-8 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap justify-center gap-4 sm:gap-12">
            {[
              { icon: Shield, text: 'Secure', color: 'text-green-600' },
              { icon: Headphones, text: 'Support', color: 'text-blue-600' },
              { icon: BadgeCheck, text: 'Quality', color: 'text-purple-600' },
              { icon: CreditCard, text: 'Refunds', color: 'text-orange-600' }
            ].map((badge, index) => {
              const Icon = badge.icon;
              return (
                <div key={index} className="flex items-center gap-1 sm:gap-2 text-gray-700">
                  <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${badge.color}`} />
                  <span className="text-xs sm:text-sm font-medium">{badge.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Courses Section */}
      {featuredCourses.length > 0 && (
        <section className="py-8 sm:py-12 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-6 sm:mb-10">
              <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2">
                Popular Courses
              </h2>
              <p className="text-sm sm:text-base text-gray-600">
                Top-rated exam preparation courses
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {featuredCourses.slice(0, 4).map((course) => (
                <Card key={course.id} className="overflow-hidden hover:shadow-xl transition-all border border-gray-200">
                  {/* Course Thumbnail - Full image visible */}
                  <div className="aspect-video bg-gradient-to-br from-blue-500 to-indigo-600 relative">
                    {course.thumbnail ? (
                      <img src={course.thumbnail} alt={course.name} className="w-full h-full object-contain bg-gray-100" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <BookOpen className="w-12 h-12 text-white/50" />
                      </div>
                    )}
                    {/* Price Badge */}
                    <div className="absolute top-2 right-2 bg-white px-2 py-1 rounded-full shadow-lg">
                      <span className="text-xs sm:text-sm font-bold text-green-600">₹{course.price || 0}</span>
                    </div>
                  </div>
                  
                  <div className="p-3 sm:p-4">
                    <h3 className="font-bold text-sm sm:text-base text-gray-900 mb-1 line-clamp-2">{course.name}</h3>
                    <p className="text-xs text-gray-600 mb-3 line-clamp-1">{course.description || 'Comprehensive exam preparation'}</p>
                    
                    {/* Course Stats */}
                    <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                      <div className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        <span>{course.test_count || 0} Tests</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <BookOpen className="w-3 h-3" />
                        <span>{course.subject_count || 0} Subjects</span>
                      </div>
                    </div>

                    <Button 
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-sm py-2"
                      onClick={onNavigateToLogin}
                    >
                      Enroll Now <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>

            <div className="text-center mt-6">
              <Button 
                variant="outline" 
                onClick={onNavigateToLogin}
                className="border-2 text-sm"
              >
                View All Courses <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Demo Test Section - Compact on mobile */}
      <section className="py-8 sm:py-12 bg-gradient-to-br from-purple-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 sm:p-10 border border-white/20">
            <div className="w-14 h-14 sm:w-20 sm:h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Play className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
            </div>
            <h2 className="text-xl sm:text-3xl font-bold text-white mb-2">
              Try Before You Buy
            </h2>
            <p className="text-sm sm:text-base text-white/80 mb-4">
              Free 5-question demo test. No signup needed!
            </p>
            <Button 
              onClick={startDemoTest}
              className="bg-white text-purple-700 hover:bg-gray-100 px-6 py-2 font-semibold"
            >
              Start Demo <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Compact */}
      {testimonials.length > 0 && (
        <section className="py-8 sm:py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-6">
              <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2">
                Student Reviews
              </h2>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="p-4 sm:p-5 bg-yellow-50 border shadow-sm">
                  <div className="flex justify-center mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm sm:text-base text-gray-700 mb-4 italic text-center">
                    "{testimonial.message}"
                  </p>
                  <div className="flex items-center justify-center gap-3 pt-3 border-t">
                    <img 
                      src={testimonial.photo_url}
                      alt={testimonial.name}
                      className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-yellow-400"
                    />
                    <div>
                      <p className="text-sm sm:text-base font-bold text-gray-900">{testimonial.name}</p>
                      <p className="text-xs sm:text-sm text-gray-600">{testimonial.designation}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Card - Compact */}
      <section className="py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Card className="p-6 sm:p-10 bg-white text-center shadow-lg border-0">
            <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2">
              Ready to Begin?
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-4">
              Join successful government employees today
            </p>
            <Button 
              onClick={onNavigateToLogin}
              className="bg-gradient-to-r from-blue-600 to-purple-600 px-8"
            >
              Get Started Free <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Card>
        </div>
      </section>

      {/* Footer */}
     

    {/*<footer className="bg-black/70 backdrop-blur-md text-white py-8 shadow-2x1 border-t border-white/50 rounded-t2x1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            {/*<div className="flex items-center gap-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_cgda-exam-prep/artifacts/bek6ttpm_logo.png" 
                alt="Departmental Exam"
                className="h-11 sm:h-10 object-contain"
              />
            </div >*/}
           {/*} <div className = "text-center sm:text-right space-y-1"></div>
            <p className="text-sm text-white/90">
              Whatsapp: 9937220652
            </p>
            <p className="text-sm text-white/90">
              email: support@departmentalexam.com
            </p> 
            <p className="text-sm text-black/80 block">
              &copy; 2025 Departmentalexam.com. All rights reserved.
            </p>
          </div>
        </div>
      </footer> */}
      <footer className="bg-black/70 backdrop-blur-md text-white py-6 shadow-2xl border-t border-white/20">
  <div className="max-w-6xl mx-auto px-4 sm:px-6">
    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">

      {/* Social Icons - All Colorful */}
      <div className="flex items-center gap-4">
        <a href="https://wa.me/919937220652" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-green-500 hover:bg-green-600 transition-all hover:scale-110" title="WhatsApp">
          <i className="fab fa-whatsapp text-xl text-white"></i>
        </a>
        <a href="mailto:support@departmentalexam.com" className="w-10 h-10 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 transition-all hover:scale-110" title="Email">
          <i className="fas fa-envelope text-lg text-white"></i>
        </a>
        <a href="https://www.facebook.com/profile.php?id=61584337342000" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 transition-all hover:scale-110" title="Facebook">
          <i className="fab fa-facebook-f text-xl text-white"></i>
        </a>
        <a href="https://instagram.com/CGDA_SAS" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 hover:from-purple-700 hover:via-pink-600 hover:to-orange-500 transition-all hover:scale-110" title="Instagram">
          <i className="fab fa-instagram text-xl text-white"></i>
        </a>
        <a href="https://www.youtube.com/@CGDASAS" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-red-600 hover:bg-red-700 transition-all hover:scale-110" title="YouTube">
          <i className="fab fa-youtube text-xl text-white"></i>
        </a>
        <a href="https://X.com/@CGDA_SAS" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-black hover:bg-gray-800 transition-all hover:scale-110 border border-white/30" title="X (Twitter)">
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        </a>
      </div>

      {/* Copyright */}
      <p className="text-xs text-white/70 text-center">
        © 2025 Departmentalexam.com. All rights reserved.
      </p>
    </div>
  </div>
</footer>

      {/* Install App Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowInstallModal(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-3 rounded-xl">
                    <Download className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Install CGDA SAS App</h2>
                    <p className="text-blue-100 text-sm">Available on all devices - No app store needed!</p>
                  </div>
                </div>
                <button onClick={() => setShowInstallModal(false)} className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Benefits */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Why Install?
                </h3>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Faster loading & offline access</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Full-screen experience</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Easy access from home screen</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    <span>No app store download needed</span>
                  </div>
                </div>
              </div>

              {/* Android Instructions */}
              <div className="border rounded-xl overflow-hidden">
                <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85a.637.637 0 0 0-.83.22l-1.88 3.24a11.43 11.43 0 0 0-8.94 0L5.65 5.67a.643.643 0 0 0-.87-.2c-.28.18-.37.54-.22.84L6.4 9.48A10.81 10.81 0 0 0 1 18h22a10.81 10.81 0 0 0-5.4-8.52M7 15.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5m10 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z"/></svg>
                    Android (Chrome, Edge, Samsung Internet)
                  </h3>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold">1</div>
                    <div>
                      <p className="font-medium">Open this website in Chrome browser</p>
                      <p className="text-sm text-gray-600">Make sure you're using Chrome, Edge, or Samsung Internet</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold">2</div>
                    <div>
                      <p className="font-medium">Tap the menu (⋮) in top-right corner</p>
                      <p className="text-sm text-gray-600">Look for three vertical dots</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold">3</div>
                    <div>
                      <p className="font-medium">Select "Install app" or "Add to Home screen"</p>
                      <p className="text-sm text-gray-600">You'll see a popup to confirm installation</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold">4</div>
                    <div>
                      <p className="font-medium">Tap "Install" or "Add"</p>
                      <p className="text-sm text-gray-600">App will appear on your home screen!</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* iOS Instructions */}
              <div className="border rounded-xl overflow-hidden">
                <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white p-4">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01M12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                    iPhone/iPad (Safari)
                  </h3>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold">1</div>
                    <div>
                      <p className="font-medium">Open this website in Safari browser</p>
                      <p className="text-sm text-gray-600">Must use Safari - won't work in Chrome on iOS</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold">2</div>
                    <div>
                      <p className="font-medium">Tap the Share button (↑) at the bottom</p>
                      <p className="text-sm text-gray-600">Square with arrow pointing up</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold">3</div>
                    <div>
                      <p className="font-medium">Scroll down and tap "Add to Home Screen"</p>
                      <p className="text-sm text-gray-600">You may need to scroll down in the menu</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold">4</div>
                    <div>
                      <p className="font-medium">Tap "Add" in top-right corner</p>
                      <p className="text-sm text-gray-600">App icon will appear on your home screen!</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop Instructions */}
              <div className="border rounded-xl overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    Desktop (Windows, Mac, Linux)
                  </h3>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold">1</div>
                    <div>
                      <p className="font-medium">Open this website in Chrome or Edge</p>
                      <p className="text-sm text-gray-600">Works on Chrome, Edge, and Brave browsers</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold">2</div>
                    <div>
                      <p className="font-medium">Look for install icon (⊕) in address bar</p>
                      <p className="text-sm text-gray-600">May also show as computer icon on right side</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold">3</div>
                    <div>
                      <p className="font-medium">Click "Install" button</p>
                      <p className="text-sm text-gray-600">App will open in its own window</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Help */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <p className="text-sm text-gray-700">
                  <strong>Need help?</strong> Contact us on WhatsApp: <a href="https://wa.me/919937220652" className="text-blue-600 hover:underline">9937220652</a>
                </p>
              </div>

              {/* Action Button */}
              <Button 
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-6 text-lg font-semibold"
                onClick={() => setShowInstallModal(false)}
              >
                Got it! Let me install
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Demo Test Modal */}
      {showDemoTest && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold">Demo Test</h3>
                  <p className="text-white/80 text-sm">
                    {demoSubmitted ? 'Results' : `Question ${demoCurrentQ + 1} of ${demoQuestions.length}`}
                  </p>
                </div>
                <button 
                  onClick={() => setShowDemoTest(false)} 
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {demoSubmitted ? (
                /* Results View */
                <div className="text-center py-8">
                  <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${demoScore >= 3 ? 'bg-green-100' : 'bg-orange-100'}`}>
                    <span className={`text-4xl font-bold ${demoScore >= 3 ? 'text-green-600' : 'text-orange-600'}`}>
                      {demoScore}/{demoQuestions.length}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {demoScore >= 4 ? 'Excellent!' : demoScore >= 3 ? 'Good Job!' : 'Keep Practicing!'}
                  </h3>
                  <p className="text-gray-600 mb-8">
                    You scored {demoScore} out of {demoQuestions.length} questions correctly.
                  </p>
                  
                  {/* Show correct answers */}
                  <div className="text-left space-y-4 mb-8">
                    {demoQuestions.map((q, idx) => {
                      const userAnswer = demoAnswers[q.id];
                      const isCorrect = userAnswer === q.correct_answer;
                      const userAnswerText = q[`option_${userAnswer}`] || 'Not answered';
                      const correctAnswerText = q[`option_${q.correct_answer}`];
                      
                      return (
                        <div key={q.id} className={`p-4 rounded-lg ${isCorrect ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                          <p className="font-medium text-gray-900 mb-2">Q{idx + 1}. {q.question_text}</p>
                          <p className="text-sm">
                            <span className="text-gray-600">Your answer: </span>
                            <span className={isCorrect ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                              {userAnswer ? `${userAnswer.toUpperCase()}. ${userAnswerText}` : 'Not answered'}
                            </span>
                          </p>
                          {!isCorrect && (
                            <p className="text-sm">
                              <span className="text-gray-600">Correct answer: </span>
                              <span className="text-green-600 font-medium">{q.correct_answer.toUpperCase()}. {correctAnswerText}</span>
                            </p>
                          )}
                          {q.explanation && (
                            <p className="text-sm mt-2 text-gray-500 italic">💡 {q.explanation}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex gap-4 justify-center">
                    <Button variant="outline" onClick={() => {
                      setDemoSubmitted(false);
                      setDemoCurrentQ(0);
                      setDemoAnswers({});
                    }}>
                      Try Again
                    </Button>
                    <Button 
                      className="bg-gradient-to-r from-purple-600 to-indigo-700"
                      onClick={() => {
                        setShowDemoTest(false);
                        onNavigateToLogin();
                      }}
                    >
                      Sign Up for Full Access <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              ) : (
                /* Question View */
                demoQuestions.length > 0 && (
                  <div>
                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
                      <div 
                        className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${((demoCurrentQ + 1) / demoQuestions.length) * 100}%` }}
                      />
                    </div>

                    {/* Question */}
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">
                        {demoQuestions[demoCurrentQ]?.question_text}
                      </h4>
                      
                      {/* Options */}
                      <div className="space-y-3">
                        {getQuestionOptions(demoQuestions[demoCurrentQ]).map((opt) => (
                          <button
                            key={opt.key}
                            onClick={() => handleDemoAnswer(demoQuestions[demoCurrentQ].id, opt.key)}
                            className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                              demoAnswers[demoQuestions[demoCurrentQ].id] === opt.key
                                ? 'border-purple-600 bg-purple-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <span className="font-medium mr-3">{opt.key.toUpperCase()}.</span>
                            {opt.value}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Navigation */}
                    <div className="flex justify-between gap-4">
                      <Button
                        variant="outline"
                        onClick={() => setDemoCurrentQ(Math.max(0, demoCurrentQ - 1))}
                        disabled={demoCurrentQ === 0}
                      >
                        Previous
                      </Button>
                      
                      {demoCurrentQ < demoQuestions.length - 1 ? (
                        <Button
                          onClick={() => setDemoCurrentQ(demoCurrentQ + 1)}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          Next
                        </Button>
                      ) : (
                        <Button
                          onClick={submitDemoTest}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Submit Test
                        </Button>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPageDesign2;
